import React, { useRef, useState } from 'react';
import { FaSort } from 'react-icons/fa';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { CHART_SERIES_COLORS } from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import {
  PieChartArea,
  PieChartLayout,
  PieLegend,
  PieLegendHeader,
  PieLegendName,
  PieLegendRow,
  PieLegendSortButton,
  PieLegendSortIndicator,
  PieLegendSwatch,
  PieLegendValue,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import {
  ChartRow,
  ChartValueFormatter,
  SortDirection,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';

const PIE_SLICE_DIMMED_OPACITY = 0.35;
const PIE_SLICE_ACTIVE_STROKE_WIDTH = 2;

interface PieChartProps {
  data: ChartRow[];
  formatValue: ChartValueFormatter;
}

function getNextValueSortDirection(
  direction: SortDirection | null,
): SortDirection | null {
  if (direction === `descending`) {
    return `ascending`;
  }

  return direction === `ascending` ? null : `descending`;
}

function sortLegendEntriesByValue<T extends { y: number }>(
  entries: T[],
  direction: SortDirection | null,
) {
  if (!direction) {
    return entries;
  }

  const sign = direction === `ascending` ? 1 : -1;

  return [...entries].sort((a, b) => sign * (a.y - b.y));
}

export function PieChart({ data, formatValue }: PieChartProps) {
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [valueSortDirection, setValueSortDirection] =
    useState<SortDirection | null>(null);
  const formattedData = data.map((row) => ({
    ...row,
    formattedY: formatValue(row.y),
  }));
  const legendEntries = sortLegendEntriesByValue(
    formattedData.map((row, originalIndex) => ({ ...row, originalIndex })),
    valueSortDirection,
  );

  const dispatchPieSliceHover = (
    index: number,
    type: `mouseover` | `mouseout`,
  ) => {
    const sector =
      chartAreaRef.current?.querySelectorAll(`.recharts-pie-sector`)[index];

    sector?.dispatchEvent(new MouseEvent(type, { bubbles: true }));
  };

  return (
    <PieChartLayout>
      <PieChartArea ref={chartAreaRef}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Tooltip formatter={formatValue} />
            <Pie
              data={formattedData}
              dataKey="y"
              nameKey="x"
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={120}
              paddingAngle={2}
              cornerRadius={3}
              isAnimationActive={false}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {formattedData.map((row, index) => {
                const isActive = activeIndex === index;
                const isDimmed = activeIndex !== null && !isActive;

                return (
                  <Cell
                    key={`${row.x}-${index}`}
                    fill={
                      CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]
                    }
                    fillOpacity={isDimmed ? PIE_SLICE_DIMMED_OPACITY : 1}
                    stroke="#fff"
                    strokeWidth={isActive ? PIE_SLICE_ACTIVE_STROKE_WIDTH : 0}
                    style={{
                      transition: `fill-opacity 150ms ease, stroke-width 150ms ease`,
                    }}
                  />
                );
              })}
            </Pie>
          </RechartsPieChart>
        </ResponsiveContainer>
      </PieChartArea>
      <PieLegend>
        <PieLegendHeader>
          <PieLegendSortButton
            type="button"
            title="Sort by value"
            onClick={() =>
              setValueSortDirection(
                getNextValueSortDirection(valueSortDirection),
              )
            }
          >
            Value
            <PieLegendSortIndicator aria-hidden>
              {valueSortDirection === `ascending` && <FiArrowUp />}
              {valueSortDirection === `descending` && <FiArrowDown />}
              {!valueSortDirection && <FaSort />}
            </PieLegendSortIndicator>
          </PieLegendSortButton>
        </PieLegendHeader>
        {legendEntries.map((row) => (
          <PieLegendRow
            key={`${row.x}-${row.originalIndex}`}
            $dimmed={activeIndex !== null && activeIndex !== row.originalIndex}
            onMouseEnter={() =>
              dispatchPieSliceHover(row.originalIndex, `mouseover`)
            }
            onMouseLeave={() =>
              dispatchPieSliceHover(row.originalIndex, `mouseout`)
            }
          >
            <PieLegendSwatch
              $color={
                CHART_SERIES_COLORS[
                  row.originalIndex % CHART_SERIES_COLORS.length
                ]
              }
            />
            <PieLegendName title={row.x}>{row.x}</PieLegendName>
            <PieLegendValue>{row.formattedY}</PieLegendValue>
          </PieLegendRow>
        ))}
      </PieLegend>
    </PieChartLayout>
  );
}
